import { NotifyHelper } from 'classes/helpers/notify.helper';
import { ISettingsDialog } from 'components/common/settings-dialog';
import { endOfToday, startOfToday, subDays } from 'date-fns';
import format from 'date-fns-tz/format';
import parseISO from 'date-fns/parseISO';
import { SessionDialogMode } from 'enums/session.enums';
import { t } from 'i18next';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { CSVHelper } from 'lib_ts/classes/csv.helper';
import { PlateHelper, VISIBLE_STRIKEZONE } from 'lib_ts/classes/plate.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { VisionHelper } from 'lib_ts/classes/vision.helper';
import { LOCAL_TIMEZONE } from 'lib_ts/enums/datetime.enums';
import { StrikeZoneType } from 'lib_ts/enums/machine.enums';
import { TrackingDevice } from 'lib_ts/enums/training.enums';
import {
  EMPTY_TRACKING_DATA,
  ICombinedData,
  ITrackingData,
} from 'lib_ts/interfaces/csv/exports/i-session-fires';
import {
  IPitchStat,
  IPitchStatsFilters,
} from 'lib_ts/interfaces/data/i-pitch-stats';
import { IMachineHardwareConfigBackup } from 'lib_ts/interfaces/i-machine-hw-config-backup';
import {
  IFireEvent,
  IMSTargetEventDataExt,
} from 'lib_ts/interfaces/i-session-event';
import { ISessionSummary } from 'lib_ts/interfaces/i-session-summary';
import { first } from 'lodash';
import { FC, ReactNode, createContext, useEffect, useState } from 'react';
import { DataService } from 'services/data.service';
import { MachinesService } from 'services/machines.service';
import { SessionEventsService } from 'services/session-events.service';
import { useAuthStore } from 'stores/use-auth-store';

const CONTEXT_NAME = 'SessionEventsContext';

const DATE_FORMAT = 'yyyy-MM-dd';
const TIME_FORMAT = 'HH:mm:ss.SSS z';

// one week
const DATE_FILTER_END = endOfToday();
const DATE_FILTER_START = subDays(startOfToday(), 7);

interface IDialogConfig {
  session: Partial<ISessionSummary>;
  mode: SessionDialogMode;
  key: number;
}

interface IFilters {
  machineIDs: string[];
  dateRange: {
    start: Date;
    end: Date;
  };
}

export interface ISessionEventsContext {
  filters: IFilters;
  readonly mergeFilters: (value: Partial<IFilters>) => void;

  sessions: ISessionSummary[];

  loading: boolean;

  fired: number;
  readonly increaseFired: () => void;

  /** undefined value =>  hidden */
  dialogSettings: ISettingsDialog | undefined;
  /** open settings from anywhere, providing an undefined config will close it */
  readonly setDialogSettings: (config: ISettingsDialog | undefined) => void;

  dialogConfig: IDialogConfig | undefined;
  setDialogConfig: (config: IDialogConfig | undefined) => void;

  /** pops the session details editor from anywhere */
  readonly showDialog: (config: IDialogConfig) => void;

  readonly getShotsData: (config: {
    machineID: string;
    session: string;
    debugTracking: boolean;
    silently?: boolean;
    szType: StrikeZoneType;
  }) => Promise<ICombinedData[]>;

  readonly getPitchStatsData: (
    filters: IPitchStatsFilters,
    silenty?: boolean
  ) => Promise<IPitchStat[]>;

  readonly fetchSessions: () => void;

  readonly reset: () => void;
}

const getDefaultFilters = (machineID: string): IFilters => {
  const output: IFilters = {
    machineIDs: [machineID],
    dateRange: {
      start: DATE_FILTER_START,
      end: DATE_FILTER_END,
    },
  };

  return output;
};

const DEFAULT: ISessionEventsContext = {
  filters: {
    machineIDs: [],
    dateRange: {
      start: DATE_FILTER_START,
      end: DATE_FILTER_END,
    },
  },
  mergeFilters: () => console.error(`${CONTEXT_NAME}: not init`),

  sessions: [],

  loading: false,

  fired: 0,
  increaseFired: () => console.error(`${CONTEXT_NAME}: not init`),

  dialogSettings: undefined,
  setDialogSettings: () => console.error(`${CONTEXT_NAME}: not init`),

  dialogConfig: undefined,
  setDialogConfig: () => console.error(`${CONTEXT_NAME}: not init`),

  showDialog: () => console.error(`${CONTEXT_NAME}: not init`),
  getShotsData: () =>
    new Promise(() => console.error(`${CONTEXT_NAME}: not init`)),
  getPitchStatsData: () =>
    new Promise(() => console.error(`${CONTEXT_NAME}: not init`)),
  fetchSessions: () => console.error(`${CONTEXT_NAME}: not init`),
  reset: () => console.error(`${CONTEXT_NAME}: not init`),
};

export const SessionEventsContext = createContext(DEFAULT);

interface IProps {
  children: ReactNode;
}

const getPrimaryTracking = (
  fire: IFireEvent | undefined,
  szType: StrikeZoneType // szType needed for determining strike/ball
): Partial<ITrackingData> | undefined => {
  const shot = first(fire?.shot);

  switch (shot?.tracking_device) {
    case TrackingDevice.RapsodoV3PRO: {
      const rapsodo = first(fire?.rapsodo);
      const msbs = first(fire?.msbs);
      return rapsodo && msbs
        ? CSVHelper.rapsodoToTracking(rapsodo, msbs)
        : undefined;
    }

    case TrackingDevice.TrackmanB1: {
      const msbs = first(fire?.msbs);
      if (
        (fire?.trackman_pitch?.data.data.Pitch ||
          fire?.trackman_hit?.data.data.Hit) &&
        msbs
      ) {
        return CSVHelper.trackmanToTracking({
          pitch: fire.trackman_pitch?.data.data.Pitch,
          hit: fire.trackman_hit?.data.data.Hit,
          msbs: msbs,
        });
      }

      return undefined;
    }

    case TrackingDevice.TrajektVision: {
      const msbs = first(fire?.msbs);
      return fire && msbs
        ? CSVHelper.visionToTracking(fire, msbs, szType)
        : undefined;
    }

    // todo: add logic here for converting from other tracking devices

    default: {
      return undefined;
    }
  }
};

export const SessionEventsProvider: FC<IProps> = (props) => {
  const current = useAuthStore((s) => s.current);
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const [sessions, setSessions] = useState(DEFAULT.sessions);

  const [loading, setLoading] = useState(DEFAULT.loading);
  const [lastFetched, setLastFetched] = useState<Date>();

  const [dialogSettings, setDialogSettings] = useState<ISettingsDialog>();
  const [dialogConfig, setDialogConfig] = useState<IDialogConfig>();

  /** used to count fire events sent for the current session */
  const [fired, setFired] = useState(DEFAULT.fired);

  const [filters, setFilters] = useState<IFilters>(
    getDefaultFilters(current.machineID)
  );

  const fetchSessions = () => setLastFetched(new Date());

  const reset = () => {
    setSessions(DEFAULT.sessions);
    setLoading(DEFAULT.loading);
    setFired(DEFAULT.fired);
    setFilters(getDefaultFilters(current.machineID));
    setDialogSettings(undefined);
    setDialogConfig(undefined);
  };

  const state: ISessionEventsContext = {
    filters: filters,
    mergeFilters: (v) => {
      setFilters((prev) => ({
        ...prev,
        ...v,
      }));
    },

    sessions: sessions,

    loading: loading,

    dialogSettings: dialogSettings,

    setDialogSettings: setDialogSettings,

    dialogConfig: dialogConfig,
    setDialogConfig: setDialogConfig,

    showDialog: (config) => {
      if (config.mode === SessionDialogMode.none) {
        setDialogConfig(undefined);
        return;
      }

      setDialogConfig(config);
    },

    fired: fired,
    increaseFired: () => setFired((prev) => prev + 1),

    getShotsData: async ({
      machineID,
      session,
      debugTracking,
      silently,
      szType,
    }) => {
      try {
        setLoading(true);

        if (!silently) {
          NotifyHelper.info({
            message_md: t('common.please-wait-request-processing'),
          });
        }

        const mstargets = await SessionEventsService.exportShots({
          machineID: machineID,
          session: session,
        });

        if (!mstargets) {
          return [];
        }

        // empty and undefined values will be stripped
        const hwConfigIDs = ArrayHelper.unique(
          mstargets.map((m) => m.data?.hw_config_id as string)
        );

        const hwConfigBackups = userRoleFeatureFlags.isSuperAdmin
          ? await MachinesService.getInstance().getMachineHWConfigs(hwConfigIDs)
          : [];

        const hwConfigDict: {
          [hw_config_id: string]: IMachineHardwareConfigBackup | undefined;
        } = {};

        hwConfigBackups.forEach((m) => {
          hwConfigDict[m._id] = m;
        });

        const output: ICombinedData[] = [];

        for (const target of mstargets.filter(
          (m) => m.fires && m.fires.length > 0
        )) {
          try {
            const targetData = target.data as IMSTargetEventDataExt;

            if (!targetData) {
              continue;
            }

            const targetCreated = parseISO(target._created);

            const baseCsv = CSVHelper.mstargetToTrajekt(targetData);

            const baseRow: ICombinedData = {
              Trajekt: {
                MachineID: target.machine,
                Session: session,
                Date: format(targetCreated, DATE_FORMAT, {
                  timeZone: LOCAL_TIMEZONE,
                }),
                Timestamp: format(targetCreated, TIME_FORMAT, {
                  timeZone: LOCAL_TIMEZONE,
                }),
                ShotNumber: baseCsv.ShotNumber ?? 0,
                Nickname: baseCsv.Nickname ?? null,
                TimeOfPitchUTC: baseCsv.TimeOfPitchUTC ?? null,
                BallType: baseCsv.BallType ?? null,
                BuildPriority: baseCsv.BuildPriority ?? null,
                InGame: baseCsv.InGame ?? null,
                Training: baseCsv.Training ?? null,
                Valid: baseCsv.Valid ?? null,
                ErrorMsg: baseCsv.ErrorMsg ?? null,
                PitchList: baseCsv.PitchList ?? null,
                TargetPlateX: baseCsv.TargetPlateX ?? null,
                TargetPlateZ: baseCsv.TargetPlateZ ?? null,
                TargetVB: baseCsv.TargetVB ?? null,
                TargetHB: baseCsv.TargetHB ?? null,
                ActualVB: baseCsv.ActualVB ?? null,
                ActualHB: baseCsv.ActualHB ?? null,
                Hitter: baseCsv.Hitter ?? null,
                HitterID: baseCsv.HitterID ?? null,
                HitterPlayerPk: baseCsv.HitterPlayerPk ?? null,
              },

              /** ensures the column is printed/accounted for in CSV output, even if first row was only from mstarget */
              Tracking: { ...EMPTY_TRACKING_DATA },

              Hardware:
                hwConfigDict[target.data?.hw_config_id ?? 'none']?.config,
            };

            /** fallback for no rapsodo data, check if machine was fired */
            const fires = target.fires ? target.fires : [];
            const outOfGameFires = fires.filter((f) => !f.data.in_game);

            /** always create a record for each fire event, using shot details where possible */
            for (const f of outOfGameFires) {
              const fireCreated = parseISO(f._created);

              const shot = first(f.shot);

              const targetBreaks =
                targetData.breaks ??
                (targetData.traj
                  ? TrajHelper.getBreaks(targetData.traj)
                  : undefined);

              const actualBreaks = shot?.traj
                ? TrajHelper.getBreaks(shot.traj)
                : undefined;

              const msbs = first(f.msbs);

              const timeOfPitch = msbs?.time
                ? // convert from UTC seconds to ms
                  new Date(msbs.time * 1_000)
                : undefined;

              const hitter = f.data.hitterExt;
              const szDef = hitter?.height_ft
                ? PlateHelper.getDynamicStrikeZone(szType, hitter.height_ft)
                : VISIBLE_STRIKEZONE;

              const row: ICombinedData = {
                Trajekt: {
                  ...baseRow.Trajekt,

                  /** overwrite mstarget date + time with shot's date + time */
                  Date: format(fireCreated, DATE_FORMAT, {
                    timeZone: LOCAL_TIMEZONE,
                  }),
                  Timestamp: format(fireCreated, TIME_FORMAT, {
                    timeZone: LOCAL_TIMEZONE,
                  }),

                  TimeOfPitchUTC: timeOfPitch?.toISOString() ?? null,

                  InGame: f.data.in_game ? 'Y' : 'N',
                  Training: f.data.training ? 'Y' : 'N',
                  Valid: f.data.rejected ? 'N' : 'Y',
                  ErrorMsg: f.data.rejected_msg ?? '',

                  TargetVB: targetBreaks?.zInches ?? null,
                  TargetHB: targetBreaks ? -targetBreaks.xInches : null,

                  ActualVB: actualBreaks?.zInches ?? null,
                  ActualHB: actualBreaks ? -actualBreaks.xInches : null,

                  PitcherFullName: targetData.pitcher ?? '',
                  PitcherPlayerPk: targetData.pitcherPk ?? 0,

                  PitchType: target.data?.pitch_type ?? '',

                  Hitter: hitter?.name ?? null,
                  HitterID: hitter?.customID ?? null,
                  HitterPlayerPk: hitter?.playerPk ?? null,
                },

                Tracking: getPrimaryTracking(f, szType) ?? baseRow.Tracking,

                DebugRapsodo: debugTracking ? first(f.rapsodo) : undefined,
                DebugTrackmanHit: debugTracking
                  ? f.trackman_hit?.data.data.Hit
                  : undefined,
                DebugTrackmanPitch: debugTracking
                  ? f.trackman_pitch?.data.data.Pitch
                  : undefined,
                DebugVision: debugTracking
                  ? VisionHelper.getShot(f, szDef)
                  : undefined,

                Hardware: baseRow.Hardware,
              };

              output.push(row);
            }
          } catch (e2) {
            console.error(e2);
          }
        }

        /** fix shot numbers */
        output.forEach((row, i) => (row.Trajekt.ShotNumber = i + 1));

        return output;
      } catch (e1) {
        console.error(e1);
        NotifyHelper.error({
          message_md:
            'Encountered an error while processing your request. Please try again later.',
        });

        return [];
      } finally {
        setLoading(false);
      }
    },

    getPitchStatsData: async (filters, silently) => {
      try {
        setLoading(true);

        if (!silently) {
          NotifyHelper.info({
            message_md: t('common.please-wait-request-processing'),
          });
        }

        return await DataService.getPitchStats(filters);
      } catch (e) {
        console.error(e);
        NotifyHelper.error({
          message_md:
            'Encountered an error while processing your request. Please try again later.',
        });
        return [];
      } finally {
        setLoading(false);
      }
    },
    fetchSessions,
    reset,
  };

  /** fetch the data at load */
  useEffect(() => {
    // context starts with an empty lastFetched and is set/re-set when the user visits Session History
    if (!lastFetched) {
      return;
    }

    (async (): Promise<void> => {
      setLoading(true);
      setSessions([]);

      const sessions = await SessionEventsService.summarizeUserSessions({
        userID: current.userID,
        startDate: filters.dateRange.start.toISOString(),
        endDate: filters.dateRange.end.toISOString(),
        machineIDs: filters.machineIDs,
      });

      if (sessions) {
        setSessions(sessions);
      } else {
        console.warn('Session events failed to load');
        setSessions([]);
      }

      setLoading(false);
    })();
  }, [
    lastFetched,
    filters.dateRange.start,
    filters.dateRange.end,
    filters.machineIDs,
  ]); // run whenever filters or lastFetched is changed

  // reset fired count when session changes
  useEffect(() => {
    setFired(DEFAULT.fired);
  }, [current.session]);

  return (
    <SessionEventsContext.Provider value={state}>
      {props.children}
    </SessionEventsContext.Provider>
  );
};
